<template>
    <div v-if="!socialShareDisabled" class="relative">
        <BaseButton
            class="text-theme-toolbar-icon"
            data-action="share"
            :disabled="isOffline"
            :tooltip="tooltipMsg"
            icon="share-nodes"
            :btn-text="btnText"
            @click="toggleShareMenu"
        />
        <transition name="slide-fade">
            <ListDropdown
                v-show="menuShareIsShowing"
                class="-right-3 text-theme-toolbar-icon"
                :submenu-display-in-down="submenuDisplayInDown"
            >
                <!-- WeChat share only is allowed for mobile because in desktop we can share a string. Only we can share a specific url using QR
                Otherwise in mobile, is necessary copy the string first and then share it. So, we can use WeChatShare -->
                <WeChatShare
                    v-if="activeWeChatShare"
                    style="max-width: 321px"
                    :url-for-share="textForShare"
                    @close="$emit('update:show-we-chat-share', false)"
                />
                <ListItemButton @click="shareIn('facebook')">
                    <svg-icon icon="facebook-icon-24px" width="22px" height="22px"></svg-icon>
                    <span class="ml-3">Facebook</span>
                </ListItemButton>
                <ListItemButton @click="shareIn('x')">
                    <svg-icon icon="x-icon-24px" width="22px" height="22px"></svg-icon>
                    <span class="ml-3">X.com</span>
                </ListItemButton>
                <ListItemButton @click="shareIn('whatsapp')">
                    <svg-icon icon="whatsapp-icon-24px" width="22px" height="22px"></svg-icon>
                    <span class="ml-3">Whatsapp</span>
                </ListItemButton>
                <ListItemButton v-if="onlyMobileVisible" @click="$emit('update:show-we-chat-share', !showWeChatShare)">
                    <svg-icon width="22px" height="22px" icon="WeChat_24"></svg-icon>
                    <span class="ml-3">WeChat</span>
                </ListItemButton>
                <ListItemButton @click="copyUrlToClipboard">
                    <div
                        class="inline-block flex h-[22px] w-[22px] items-center justify-center rounded bg-theme-accent"
                    >
                        <svg-icon icon="copy-link" width="16px" height="16px" class="m-auto text-theme-accent-on" />
                    </div>
                    <span class="ml-3">{{ localizations[lang].copyUrl }}</span>
                </ListItemButton>
            </ListDropdown>
        </transition>
    </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import ListDropdown from '../../Components/list/ListDropdown.vue';
import ListItemButton from '../../Components/list/ListItemButton.vue';
import WeChatShare from '../../SocialShare/WeChatShare.vue';
import BaseButton from '../../Components/buttons/BaseButton.vue';
import SocialNeworksLinksTemplates from '_shared_/js/utils/SocialNeworksLinksTemplates.json';

export default {
    components: {
        BaseButton,
        WeChatShare,
        ListDropdown,
        ListItemButton,
    },
    props: {
        menuShareIsShowing: {
            type: Boolean,
            required: true,
        },
        showWeChatShare: {
            type: Boolean,
            required: true,
        },
        btnText: {
            type: String,
            required: false,
            default: null,
        },
        submenuDisplayInDown: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['update:show-we-chat-share', 'toggle-share-menu'],
    computed: {
        ...mapGetters(['currentPage', 'isEpub', 'onlyMobileVisible', 'socialShareDisabled']),
        ...mapState([
            'issue',
            'title',
            'localizations',
            'lastLocation',
            'currentHighlight',
            'lang',
            'aiActions',
            'isOffline',
        ]),
        activeWeChatShare() {
            return this.showWeChatShare && this.onlyMobileVisible;
        },
        tooltipMsg() {
            return this.btnText ? null : this.localizations[this.lang].tooltips.share;
        },
    },
    methods: {
        ...mapMutations(['openToast']),
        ...mapActions(['copyToClipBoard', 'trackEvent', 'buildUrlAndGoToSocialNetworksSharing']),
        async copyUrlToClipboard() {
            const location = this.isEpub ? btoa(JSON.stringify(this.lastLocation.epub)) : this.currentPage;
            const wasCopied = await this.copyToClipBoard(`${this.issue.issue_url}?location=${location}`);
            if (!wasCopied) return;

            this.openToast({ text: this.localizations[this.lang].copyUrlToClipboard });
            this.toggleShareMenu();
        },
        issueHighlightUrlForShare() {
            const location = this.isEpub ? btoa(JSON.stringify(this.lastLocation.epub)) : this.currentPage;
            return `${this.issue.issue_url}?location=${location}`;
        },
        shareIn(network) {
            const url = SocialNeworksLinksTemplates.heighlight[network];
            const title = this.truncateLongText(this.title, 100);
            const authors = Object.prototype.hasOwnProperty.call(this.issue.tags, 'author')
                ? this.issue.tags.author.join(', ')
                : null;

            this.buildUrlAndGoToSocialNetworksSharing({
                url,
                network,
                title,
                authors,
                highlight: `"${this.currentHighlight.totalText}"`,
                readerUrl: this.issueHighlightUrlForShare,
            });
            this.toggleShareMenu();
        },
        textForShare() {
            return `"${this.currentHighlight.totalText} ${this.truncateLongText(this.title, 100)}" ${
                this.localizations[this.lang].in
            } ${this.issueHighlightUrlForShare}`;
        },
        truncateLongText(text, length = 140) {
            if (text.length > length) {
                return `${text.substring(0, length)}...`;
            }

            return text;
        },
        toggleShareMenu() {
            this.$emit('toggle-share-menu');
        },
    },
};
</script>
